<app-config-top-nav></app-config-top-nav>

<div class="rounding-rules-container">
  <!-- Loading -->
  <div *ngIf="isLoading" class="spinner-container">
    <div class="spinner"></div>
  </div>

  <!-- Error -->
  <div *ngIf="errorMessage" class="error-message">{{ errorMessage }}</div>

  <!-- Header -->
  <div class="header" *ngIf="!isLoading">
    <div class="header-copy">
      <h2>Rounding Rules</h2>
    </div>

    <div class="header-actions">
      <button class="btn btn-primary" (click)="openCreateModal()">+ New Rounding Rule</button>
    </div>
  </div>

  <!-- Sticky Pagination -->
  <div class="pagination sticky" *ngIf="!isLoading && !errorMessage">
    <div class="page-controls">
      <button (click)="changePage(currentPage - 1)" [disabled]="currentPage === 1">Prev</button>
      <span>Page {{ currentPage }} of {{ totalPages }}</span>
      <button (click)="changePage(currentPage + 1)" [disabled]="currentPage === totalPages">
        Next
      </button>
    </div>

    <div class="page-size">
      <label for="pageSize">Page Size:</label>
      <select id="pageSize" [(ngModel)]="pageSize" (change)="changePageSize(pageSize)">
        <option *ngFor="let size of pageSizes" [value]="size">{{ size }}</option>
      </select>
    </div>
  </div>

  

  <!-- Rules List -->
  <div class="rounding-rules-list" *ngIf="!isLoading && !errorMessage">
    <div
      *ngFor="let rule of pagedRoundingRules; trackBy: trackByRuleId"
      class="rounding-rule-row"
      [class.is-default]="rule.isDefault"
    >
      <div class="rounding-rule-main">
        <div class="top-line">
          <div class="title-wrap">
            <div class="rule-title">
              {{ rule.payGroup?.name || '—' }}
            </div>

            <span *ngIf="rule.isDefault" class="default-pill">Default</span>
          </div>
        </div>

        <div class="rule-details">
          <div class="detail-item">
            <span class="label">Rounding Interval</span>
            <span class="value">{{ formatDuration(rule.roundingInterval) }}</span>
          </div>

          <div class="detail-item">
            <span class="label">Break Threshold</span>
            <span class="value">{{ formatThresholdHours(rule.breakThresholdHours) }}</span>
          </div>

          <div class="detail-item">
            <span class="label">Break Time</span>
            <span class="value">{{ formatDuration(rule.breakTime) }}</span>
          </div>

          <div class="detail-item">
            <span class="label">Last Updated By</span>
            <span class="value">{{ getUpdatedByLabel(rule) }}</span>
          </div>

          <div class="detail-item">
            <span class="label">Department</span>
            <span class="value">{{ getDepartmentCodeLabel(rule) }}</span>
          </div>
        </div>
      </div>

      <div class="rounding-rule-actions">
        <button class="btn make-default" (click)="confirmMakeDefault(rule)" [disabled]="rule.isDefault">
          {{ rule.isDefault ? 'Default' : 'Make Default' }}
        </button>

        <button class="btn edit" (click)="openEditModal(rule)">Edit</button>
        <button class="btn delete" (click)="confirmDelete(rule)">Delete</button>
      </div>
    </div>

    <div class="empty-state" *ngIf="pagedRoundingRules.length === 0">
      No rounding rules found.
    </div>
  </div>

  <!-- Create/Edit Modal -->
  <div *ngIf="modalOpen" class="modal-backdrop">
    <div class="modal rounding-rule-modal" (click)="$event.stopPropagation()">
      <div class="modal-header">
        <div>
          <h2>{{ isEditMode ? 'Edit Rounding Rule' : 'New Rounding Rule' }}</h2>
          <p class="modal-subtitle">
            Configure the pay group, scope, and duration-based rounding settings.
          </p>
        </div>
      </div>

      <form #roundingRuleForm="ngForm" (ngSubmit)="submitRule(roundingRuleForm)" class="rule-form">
        <div class="form-section">

          <div class="form-grid two-col">
            <div class="field">
              <label for="payGroupId">Pay Group</label>
              <select id="payGroupId" [(ngModel)]="formModel.payGroupId" name="payGroupId" required>
                <option [ngValue]="null">Select pay group</option>
                <option *ngFor="let payGroup of payGroups" [ngValue]="payGroup.id">
                  {{ payGroup.name }}
                </option>
              </select>
            </div>

            <div class="field">
              <label for="departmentId">Department</label>
              <select id="departmentId" [(ngModel)]="formModel.departmentId" name="departmentId">
                <option [ngValue]="null">All Departments</option>
                <option *ngFor="let dept of departments" [ngValue]="dept.id">
                  {{ dept.code }} - {{ dept.name }}
                </option>
              </select>
            </div>
          </div>
        </div>

        <div class="form-section">
          <div class="duration-stack">
            <div class="duration-card">
              <div class="duration-card-head">
                <label>Rounding Interval</label>
              </div>

              <div class="duration-group compact">
                <div class="duration-input">
                  <span class="mini-label">Minutes</span>
                  <div class="input-shell">
                    <input
                      type="number"
                      min="0"
                      step="1"
                      [(ngModel)]="roundingIntervalMinutes"
                      name="roundingIntervalMinutes"
                      required
                    />
                    <span class="unit">min</span>
                  </div>
                </div>
              </div>
            </div>

            <div class="duration-card">
              <div class="duration-card-head">
                <label>Break Time</label>
              </div>

              <div class="duration-group">
                <div class="duration-input">
                  <span class="mini-label">Hours</span>
                  <div class="input-shell">
                    <input
                      type="number"
                      min="0"
                      step="1"
                      [(ngModel)]="breakTimeHours"
                      name="breakTimeHours"
                      required
                    />
                    <span class="unit">hr</span>
                  </div>
                </div>

                <div class="duration-input">
                  <span class="mini-label">Minutes</span>
                  <div class="input-shell">
                    <input
                      type="number"
                      min="0"
                      max="59"
                      step="1"
                      [(ngModel)]="breakTimeMinutes"
                      name="breakTimeMinutes"
                      required
                    />
                    <span class="unit">min</span>
                  </div>
                </div>
              </div>
            </div>

            <div class="duration-card">
              <div class="duration-card-head">
                <label for="breakThresholdHours">
                  Break Threshold Hours
                  <span class="tooltip-container">
                    <img src="/info-icon.webp" alt="Info" class="tooltip-icon" />
                    <span class="tooltip-text">
                      The number of worked hours before the break time applies.
                    </span>
                  </span>
                </label>
              </div>

              <div class="threshold-shell">
                <input
                  id="breakThresholdHours"
                  type="number"
                  step="0.25"
                  min="0"
                  [(ngModel)]="formModel.breakThresholdHours"
                  name="breakThresholdHours"
                  required
                />
                <span class="unit">hrs</span>
              </div>
            </div>
          </div>
        </div>

        <div class="modal-buttons">
          <button type="button" class="btn btn-secondary" (click)="closeModal()" [disabled]="isSaving">
            Cancel
          </button>

          <button type="submit" class="btn btn-primary save-btn" [disabled]="isSaving">
            <span class="btn-spinner" *ngIf="isSaving"></span>
            <span>{{ isEditMode ? 'Update' : 'Create' }}</span>
          </button>
        </div>
      </form>
    </div>
  </div>

  <!-- Delete Modal -->
  <div *ngIf="deleteConfirmOpen" class="modal-backdrop">
    <div class="modal confirm-modal" (click)="$event.stopPropagation()">
      <h2>Delete Rounding Rule</h2>
      <p class="modal-text">
        Are you sure you want to delete this rounding rule for
        <strong>{{ ruleToDelete?.payGroup?.name }}</strong>?
      </p>

      <div class="modal-buttons confirm-buttons">
        <button type="button" class="btn btn-secondary" (click)="cancelDelete()" [disabled]="isSaving">
          Cancel
        </button>
        <button type="button" class="btn btn-danger" (click)="deleteRule()" [disabled]="isSaving">
          <span class="btn-spinner" *ngIf="isSaving"></span>
          <span>Delete</span>
        </button>
      </div>
    </div>
  </div>

  <!-- Make Default Modal -->
  <div *ngIf="defaultConfirmOpen" class="modal-backdrop">
    <div class="modal confirm-modal" (click)="$event.stopPropagation()">
      <h2>Make Default Rule</h2>
      <p class="modal-text">
        Make the rounding rule for <strong>{{ ruleToMakeDefault?.payGroup?.name }}</strong> the new default?
      </p>
      <p class="modal-subtext">Only one rounding rule can be the default at a time.</p>

      <div class="modal-buttons confirm-buttons">
        <button type="button" class="btn btn-secondary" (click)="cancelMakeDefault()" [disabled]="isSaving">
          Cancel
        </button>
        <button type="button" class="btn btn-primary" (click)="makeDefaultRule()" [disabled]="isSaving">
          <span class="btn-spinner" *ngIf="isSaving"></span>
          <span>Make Default</span>
        </button>
      </div>
    </div>
  </div>

  <!-- Toast -->
  <div *ngIf="toastVisible" class="toast" [ngClass]="toastType">
    <img class="icon" alt="icon" />
    <span>{{ toastMessage }}</span>
  </div>
</div>